// Keeps the query cache in step with the person's events.
//
// Promise:
// - One stream per window (`/api/me/events`). Each event goes to every handler
//   the parts registered for its type, in the order of the parts; a type no one
//   registered is ignored. Screens never subscribe themselves.
// - The first `ready`: queries that already exist were fetched (or started)
//   before the stream began, and may have missed what committed in between, so
//   they are fetched again once. Later reconnects resume after the last cursor
//   and fetch nothing.
// - When the server refuses the cursor, every query is fetched again and the
//   stream goes on from the head.
import type { QueryClient } from "@tanstack/react-query";
import type { Api } from "../data/api";
import {
  type EventStream,
  openEventStream,
  type StreamedEvent,
} from "./event-stream";

export interface RealtimeContext {
  qc: QueryClient;
  /** For a handler that reads exactly what changed and puts it in place. */
  api: Api;
  event: StreamedEvent;
}

export type RealtimeHandler = (
  payload: unknown,
  context: RealtimeContext,
) => void;

export type RealtimeHandlers = Readonly<Record<string, RealtimeHandler>>;

export interface StartRealtimeOptions {
  qc: QueryClient;
  api: Api;
  handlers: readonly RealtimeHandlers[];
  url?: string;
  fetch?: typeof fetch;
  /** For tests: the stream to use instead of EventSource. */
  open?: typeof openEventStream;
  onError?(error: unknown, event: StreamedEvent): void;
}

export function mergeHandlers(
  handlers: readonly RealtimeHandlers[],
): Map<string, RealtimeHandler[]> {
  const byType = new Map<string, RealtimeHandler[]>();
  for (const set of handlers)
    for (const [type, handler] of Object.entries(set)) {
      const list = byType.get(type) ?? [];
      list.push(handler);
      byType.set(type, list);
    }
  return byType;
}

export function startMeRealtime(options: StartRealtimeOptions): EventStream {
  const { qc, api } = options;
  const byType = mergeHandlers(options.handlers);
  let ready = false;
  const open = options.open ?? openEventStream;
  return open({
    url: options.url,
    fetch: options.fetch,
    types: [...byType.keys()],
    onReady() {
      if (ready) return;
      ready = true;
      void qc.invalidateQueries();
    },
    onCursorRejected() {
      void qc.invalidateQueries();
    },
    onEvent(event) {
      for (const handler of byType.get(event.type) ?? [])
        try {
          handler(event.payload, { qc, api, event });
        } catch (error) {
          // One part's mistake must not stop the others from hearing the event.
          options.onError?.(error, event);
        }
    },
  });
}
